/**
 * Kommunernas offentliga adresser (/skolor/<adress>).
 *
 * Regel: adressen görs av kommunnamnet (å, ä, ö bort). Krockar två kommuner
 * får BÅDA länet efter namnet (habo-uppsala-lan, habo-jonkopings-lan), och
 * den korta adressen blir en valsida med länkar till båda. Ingen
 * vidareskickning — den korta adressen kan vara indexerad.
 *
 * Databasens municipality_slug lämnas orörd (importen skriver den); krocken
 * löses här genom att kombinera den med county_slug.
 */
import kommunerData from "./kommuner-data.json";
import { slugify } from "./skolverket/geo";

export type Kommun = { code: string; name: string; countySlug: string };

export const KOMMUNER: Kommun[] = (kommunerData as [string, string, string][]).map(
  ([code, name, countySlug]) => ({ code, name, countySlug }),
);

const COUNTY_NAME: Record<string, string> = {};

function buildIndex(list: Kommun[]) {
  const byShort = new Map<string, Kommun[]>();
  for (const k of list) {
    const s = slugify(k.name);
    byShort.set(s, [...(byShort.get(s) ?? []), k]);
  }
  return byShort;
}

const BY_SHORT = buildIndex(KOMMUNER);

/** Korta adresser som delas av flera kommuner. */
export function ambiguousShortSlugs(list: Kommun[] = KOMMUNER): string[] {
  return [...buildIndex(list).entries()].filter(([, v]) => v.length > 1).map(([s]) => s);
}

/** Offentlig adress för en kommun utifrån databasens slug + länets slug. */
export function kommunAdress(
  municipalitySlug: string | null | undefined,
  countySlug: string | null | undefined,
): string | null {
  if (!municipalitySlug) return null;
  const hits = BY_SHORT.get(municipalitySlug);
  if (hits && hits.length > 1 && countySlug) return `${municipalitySlug}-${countySlug}`;
  return municipalitySlug;
}

/** Alla offentliga kommunadresser — används av provet mot krockar. */
export function allaKommunAdresser(list: Kommun[] = KOMMUNER): string[] {
  const idx = buildIndex(list);
  return list.map((k) => {
    const s = slugify(k.name);
    return (idx.get(s)?.length ?? 0) > 1 ? `${s}-${k.countySlug}` : s;
  });
}

export type KommunUppslag =
  | { kind: "kommun"; municipalitySlug: string; countySlug: string | null }
  | { kind: "val"; shortSlug: string; kommuner: Array<Kommun & { adress: string; countyName: string }> };

/** Tolkar en adress: en kommun (med ev. länsfilter) eller en valsida. */
export function tolkaKommunAdress(adress: string): KommunUppslag {
  const a = adress.toLowerCase();
  const hits = BY_SHORT.get(a);
  if (hits && hits.length > 1) {
    return {
      kind: "val",
      shortSlug: a,
      kommuner: hits.map((k) => ({ ...k, adress: `${a}-${k.countySlug}`, countyName: lanNamn(k.countySlug) })),
    };
  }
  for (const short of ambiguousShortSlugs()) {
    for (const k of BY_SHORT.get(short)!) {
      if (a === `${short}-${k.countySlug}`) {
        return { kind: "kommun", municipalitySlug: short, countySlug: k.countySlug };
      }
    }
  }
  return { kind: "kommun", municipalitySlug: a, countySlug: null };
}

export function lanNamn(countySlug: string): string {
  if (COUNTY_NAME[countySlug]) return COUNTY_NAME[countySlug];
  return countySlug;
}

// Fyll länsnamnen ur geo-tabellen.
import { COUNTIES } from "./skolverket/geo";
for (const c of Object.values(COUNTIES)) COUNTY_NAME[c.slug] = c.name;

/**
 * Kommunkoden (0305, 0643) är kommunens identitet. Allt som grupperar,
 * räknar eller filtrerar per kommun nycklas på den — aldrig på adressen
 * eller namnet, som två kommuner kan dela (Håbo/Habo).
 *
 * Tar emot en kod, en offentlig adress (habo-uppsala-lan) eller en kort
 * adress (stockholm). Ger null när adressen är okänd eller delas av flera
 * kommuner — då finns ingen enskild kommun att välja.
 */
export function kommunKod(adressEllerKod: string | null | undefined): string | null {
  const a = (adressEllerKod ?? "").trim().toLowerCase();
  if (!a) return null;
  if (/^\d{4}$/.test(a)) return KOMMUNER.some((k) => k.code === a) ? a : null;
  const t = tolkaKommunAdress(a);
  if (t.kind === "val") return null;
  const hits = (BY_SHORT.get(t.municipalitySlug) ?? []).filter(
    (k) => !t.countySlug || k.countySlug === t.countySlug,
  );
  return hits.length === 1 ? hits[0].code : null;
}

/** Offentlig adress för en kommunkod. */
export function adressForKod(code: string | null | undefined): string | null {
  const k = KOMMUNER.find((x) => x.code === code);
  if (!k) return null;
  return kommunAdress(slugify(k.name), k.countySlug);
}

// regelfil: src/AGENTS.md
export type KommunValResultat =
  | { kind: "kod"; code: string }
  | { kind: "val"; kommuner: Array<Kommun & { adress: string; countyName: string }> }
  | { kind: "okand" };

/**
 * Regeln för kommunfiltret: det tar kommunkod. En adressform godtas bara
 * när den pekar på exakt en kommun. En delad adress ("habo") ger valet
 * mellan kommunerna, med län — aldrig en blandad skollista.
 */
export function kommunVal(value: string | null | undefined): KommunValResultat {
  const code = kommunKod(value);
  if (code) return { kind: "kod", code };
  const a = (value ?? "").trim().toLowerCase();
  if (a && !/^\d{4}$/.test(a)) {
    const t = tolkaKommunAdress(a);
    if (t.kind === "val") return { kind: "val", kommuner: t.kommuner };
  }
  return { kind: "okand" };
}

/** Värde som aldrig matchar någon skola — används när ingen enskild kommun pekas ut. */
export const INGEN_KOMMUN = "ingen-kommun";

/**
 * Filtrerar en fråga mot education_units på kommunkoden. Pekar värdet inte
 * ut exakt en kommun ger filtret inga skolor; anroparen visar då valet.
 */
// deno-lint-ignore no-explicit-any
export function kommunFilter<Q extends { eq: (col: string, v: string) => any }>(q: Q, value: string): Q {
  return q.eq("municipality_code", kommunKod(value) ?? INGEN_KOMMUN);
}

/** Kommunargumentet till sökfunktionen i databasen: kod, eller ett värde utan träffar. */
export function kommunSokArg(value: string | null | undefined): string | undefined {
  if (!(value ?? "").trim()) return undefined;
  return kommunKod(value) ?? INGEN_KOMMUN;
}
